// Canonical Career ROI calculation — SINGLE SOURCE OF TRUTH.
//
// This file is the only place in the codebase where Career ROI and Breakeven
// are computed. Every screen (Financial, Decision Report, AI Recommendations,
// University Detail, Dashboard) imports from here. Do NOT add another formula.
//
// Formula:
//   Career ROI multiple = (median graduate annual salary × WORKING_YEARS_HORIZON)
//                         ÷ total programme investment
//   Breakeven years     = total programme investment ÷ median graduate annual salary
//
// Inputs:
//   - medianGraduateSalaryInr  Per-university published median early-career salary
//                              (INR/yr). MUST be a real figure from the institution's
//                              graduate-outcomes survey. If missing → "—" everywhere.
//   - totalInvestmentInr       All-in programme cost (tuition + living + one-time) for
//                              the full programme duration, inflation-adjusted, INR.
//
// Assumptions (documented):
//   - WORKING_YEARS_HORIZON = 10 years (a conservative early-career window common in
//     ROI literature; longer horizons inflate the multiple, shorter understate it).
//   - We do NOT apply salary growth here; salary is held constant at the published
//     median for the horizon. This keeps ROI transparent and comparable across unis.

export const WORKING_YEARS_HORIZON = 10;

/**
 * Returns { multiple, hasData } for a single university.
 * `multiple` is null when we lack a real published salary figure.
 */
export function calculateCareerRoi({ medianGraduateSalaryInr, totalInvestmentInr }) {
  if (!medianGraduateSalaryInr || !totalInvestmentInr) {
    return { multiple: null, hasData: false };
  }
  const multiple = (medianGraduateSalaryInr * WORKING_YEARS_HORIZON) / totalInvestmentInr;
  return { multiple, hasData: true };
}

/** Returns the Career ROI as "X.Xx" or "—" when no salary data exists. */
export function formatCareerRoi({ medianGraduateSalaryInr, totalInvestmentInr }) {
  const { multiple, hasData } = calculateCareerRoi({ medianGraduateSalaryInr, totalInvestmentInr });
  if (!hasData) return "—";
  return `${multiple.toFixed(1)}x`;
}

/** Years until graduate salary recoups the full investment; null when no data. */
export function calculateBreakevenYears({ medianGraduateSalaryInr, totalInvestmentInr }) {
  if (!medianGraduateSalaryInr || !totalInvestmentInr) return null;
  return totalInvestmentInr / medianGraduateSalaryInr;
}

/** Returns the breakeven as "X.X yr" or "—" when no salary data exists. */
export function formatBreakeven({ medianGraduateSalaryInr, totalInvestmentInr }) {
  const years = calculateBreakevenYears({ medianGraduateSalaryInr, totalInvestmentInr });
  if (years === null) return "—";
  return `${years.toFixed(1)} yr`;
}

/** True when we lack a real median-salary figure → screens should show an EST. badge. */
export function isCareerRoiEstimate({ medianGraduateSalaryInr }) {
  return !medianGraduateSalaryInr;
}
